import { useId } from 'react';
import type { BranchOption, DeviceOption } from '@order-pilot/shared';
import { IconBranch, IconStore } from '@/components/Icons';
import { DateRangeField } from '@/features/portal/DateRangeField';
import type { ReportFiltersState } from '@/features/portal/filters';
import { RANGE_PRESETS } from '@/features/portal/filters';
import { formatPosDate } from '@/lib/format';

export interface FilterBarProps {
  filters: ReportFiltersState;
  branches: readonly BranchOption[];
  /** Hidden entirely for single-branch tenants. */
  showBranch: boolean;
  devices: readonly DeviceOption[];
  /** Hidden entirely for single-till tenants — most cafes. */
  showDevice: boolean;
  /** Announced politely while any report query is in flight. */
  busy: boolean;
}

function presetClass(active: boolean): string {
  const base =
    'rounded-md px-2.5 py-1.5 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600 focus-visible:ring-offset-1';
  return active ? `${base} bg-white text-brand-800 shadow-sm` : `${base} text-slate-600 hover:text-slate-900`;
}

/**
 * Sticky report chrome. Every control writes straight to the URL query string
 * (via `filters`), which is what the report queries are keyed on — so a change
 * here refetches, and the address bar always describes what is on screen.
 */
export function FilterBar({
  filters,
  branches,
  showBranch,
  devices,
  showDevice,
  busy,
}: FilterBarProps) {
  const branchId = useId();
  const deviceId = useId();
  const { filters: value, setPreset, setRange, setBranch, setDevice, today } = filters;

  // Selecting a branch clears the till (see `setBranch`), so only offer tills
  // that could actually be selected under the current branch — otherwise the
  // list advertises options that reset the branch the moment they are chosen.
  const selectableDevices =
    value.branchId === null
      ? devices
      : devices.filter(
          (device) =>
            branches.find((branch) => branch.id === value.branchId)?.name ===
            device.branchName,
        );

  return (
    <div className="sticky top-14 z-20 border-b border-slate-200 bg-white/95 backdrop-blur md:top-0">
      <div className="mx-auto flex w-full max-w-7xl flex-wrap items-center gap-3 px-4 py-3 sm:px-6 lg:px-8">
        <div role="group" aria-label="Date range preset" className="flex flex-wrap gap-1 rounded-lg bg-slate-100 p-1">
          {RANGE_PRESETS.map((preset) => (
            <button
              key={preset.id}
              type="button"
              aria-pressed={value.preset === preset.id}
              className={presetClass(value.preset === preset.id)}
              onClick={() => setPreset(preset.id)}
            >
              {preset.label}
            </button>
          ))}
        </div>

        <DateRangeField value={value} today={today} onChange={setRange} />

        {showBranch ? (
          <div className="flex items-center gap-2">
            <IconBranch className="h-4 w-4 shrink-0 text-slate-400" />
            <label htmlFor={branchId} className="text-xs font-medium text-slate-500">
              Branch
            </label>
            <select
              id={branchId}
              className="input h-9 w-44 py-1 pr-8 text-xs"
              value={value.branchId ?? ''}
              onChange={(event) => setBranch(event.target.value === '' ? null : event.target.value)}
            >
              <option value="">All branches</option>
              {branches.map((branch) => (
                <option key={branch.id} value={branch.id}>
                  {branch.name}
                </option>
              ))}
            </select>
          </div>
        ) : null}

        {showDevice ? (
          <div className="flex items-center gap-2">
            <IconStore className="h-4 w-4 shrink-0 text-slate-400" />
            <label htmlFor={deviceId} className="text-xs font-medium text-slate-500">
              Till
            </label>
            <select
              id={deviceId}
              className="input h-9 w-44 py-1 pr-8 text-xs"
              value={value.deviceId ?? ''}
              onChange={(event) => setDevice(event.target.value === '' ? null : event.target.value)}
            >
              {/* Default is every till summed. Sound because each till's rows are
                  keyed by (device_id, source_id) and every report join carries
                  device_id — see deviceFilter on the server. */}
              <option value="">All tills</option>
              {selectableDevices.map((device) => (
                <option key={device.id} value={device.id}>
                  {device.name}
                  {device.isActive ? '' : ' (revoked)'}
                </option>
              ))}
            </select>
          </div>
        ) : null}

        <p className="ml-auto text-xs text-slate-400" aria-live="polite">
          {busy ? 'Updating…' : `${formatPosDate(value.from)} – ${formatPosDate(value.to)}`}
        </p>
      </div>
    </div>
  );
}
